import sensors from '../../../utils/sensors'
import { store } from '../../../app/store'

const EVENT_NAME = 'workgroup_b8128_click'
/**
 * 1125 迭代 · 三事件（b8128 列表页 / d8129 AI 助手 / d8130 详情页）统一升级：
 * 均新增 number2/number3（文件夹已有数/上限数），d8129 另增 payToCreate
 */
const REQUIREMENT_ID = 1125
const TRACK_SIGN = 'aworkgroup.b8128.click.workgroup_b8128_click'

const SORT_LABEL_MAP: Record<string, string> = {
  'folderName:asc': '按文件夹名 升序',
  'folderName:desc': '按文件夹名 降序',
  'photoCount:desc': '按照片数 从多到少',
  'photoCount:asc': '按照片数 从少到多',
  'subfolderCount:desc': '按子文件夹数 从多到少',
  'subfolderCount:asc': '按子文件夹数 从少到多',
  'updateTime:desc': '按更新时间 从近到远',
  'updateTime:asc': '按更新时间 从远到近',
  'ctime:desc': '按创建时间 从近到远',
  'ctime:asc': '按创建时间 从远到近'
}

/**
 * number2/number3 · 文件夹已有数/上限数，取接口4 配额（folderQuotaSlice）。
 * b8128（列表页）与 d8129（AI 助手）两事件共用。
 * 仅在已加载成功时上报：缺失=未知、0=真为零，避免页面初始 0/0 污染统计。
 * cap=-1 是超级/企业"无上限"哨兵，按服务端原始值上报
 */
function getQuotaProps(state: any): Record<string, number> {
  const quota = state.folderQuota
  if (quota?.loaded && !quota?.error) {
    return { number2: quota.used, number3: quota.cap }
  }
  return {}
}

function getCommonProps() {
  const state = store.getState() as any
  const aiClassify = state.aiClassify || {}
  const viewMode = aiClassify.ui?.viewMode || 'grid'
  const folderSort = aiClassify.ui?.folderSort || {
    key: 'updateTime',
    order: 'desc'
  }
  const sortKey = `${folderSort.key}:${folderSort.order}`
  return {
    pageType: viewMode === 'grid' ? 'gridView' : 'listView',
    method: SORT_LABEL_MAP[sortKey] || sortKey,
    ...getQuotaProps(state)
  }
}

export type ClassifyActionType =
  | 'enterPage'
  | 'createFolder'
  | 'confirmCreate'
  | 'selectOne'
  | 'enterOne'
  | 'download'
  | 'moveTo'
  | 'delete'
  | 'cancelDelete'
  | 'confirmDelete'
  | 'cancel'
  | 'selectAll'
  | 'unSelectAll'
  | 'categoryTask'
  | 'downloadRecord'
  | 'aiAssistant'
  | 'recycleBin'

export function trackClassifyClick(
  actionType: ClassifyActionType,
  extra?: { folderName?: string }
) {
  const common = getCommonProps()
  const params: Record<string, any> = {
    requirement_id: REQUIREMENT_ID,
    track_sign: TRACK_SIGN,
    actionType,
    ...common
  }
  if (extra?.folderName) {
    params.folderName = extra.folderName
  }
  sensors.track(EVENT_NAME, params)
}

// ─── 第三个事件：AI 智能分类助手 ───

const AI_CHAT_EVENT = 'workgroup_b8128_d8129_click'
const AI_CHAT_TRACK_SIGN =
  'aworkgroup.b8128.d8129.click.workgroup_b8128_d8129_click'

export type AIChatActionType =
  | 'enterPage'
  | 'conversation'
  | 'content'
  | 'send'
  | 'confirmCreate'
  // 点击付费创建 · 确认按钮命中商业化卡点（非会员/超额）弹开通窗时
  | 'payToCreate'
  | 'split'
  | 'closeAI'
  | 'history'
  | 'closeHistory'
  | 'switchSession'
  | 'startNewConversation'
  // 图像识别内测问卷：点击入口横幅
  | 'surveyEntry'

function getAIChatCommonProps() {
  const state = store.getState() as any
  const sessionId = state.aiClassify?.session?.conversationId || ''
  const path = window.location.pathname || '/'
  return { conversationID: sessionId, path, ...getQuotaProps(state) }
}

export function trackAIChatClick(
  actionType: AIChatActionType,
  extra?: {
    string1?: string
    string2?: string
    list1?: string[]
    number1?: number
  }
) {
  const common = getAIChatCommonProps()
  const params: Record<string, any> = {
    requirement_id: REQUIREMENT_ID,
    track_sign: AI_CHAT_TRACK_SIGN,
    actionType,
    ...common
  }
  if (extra?.string1 !== undefined) params.string1 = extra.string1
  if (extra?.string2 !== undefined) params.string2 = extra.string2
  if (extra?.list1 !== undefined) params.list1 = extra.list1
  if (extra?.number1 !== undefined) params.number1 = extra.number1
  sensors.track(AI_CHAT_EVENT, params)
}

// ─── 第四个事件：子级文件夹页（详情页） ───

const DETAIL_EVENT = 'workgroup_b8128_d8130_click'
const DETAIL_TRACK_SIGN =
  'aworkgroup.b8128.d8130.click.workgroup_b8128_d8130_click'

export type DetailActionType =
  | 'enterPage'
  | 'selectFolder'
  | 'enterFolder'
  | 'viewOne'
  | 'multiSelect'
  | 'createSubFolder'
  | 'addPhoto'
  | 'download'
  | 'downloadAsZip'
  | 'downloadAsExcel'
  | 'downloadRecord'
  | 'editWatermark'
  | 'removeWatermark'
  | 'remove'
  | 'moveTo'
  | 'delete'
  | 'cancel'
  | 'selectAll'
  | 'unSelectAll'
  | 'aiAssistant'

export function trackDetailClick(
  actionType: DetailActionType,
  extra?: { path?: string; pageType?: string }
) {
  const state = store.getState() as any
  const viewMode = state.aiClassify?.ui?.viewMode || 'grid'
  const params: Record<string, any> = {
    requirement_id: REQUIREMENT_ID,
    track_sign: DETAIL_TRACK_SIGN,
    actionType,
    pageType:
      extra?.pageType || (viewMode === 'grid' ? 'gridView' : 'listView'),
    path: extra?.path || '/',
    ...getQuotaProps(state)
  }
  sensors.track(DETAIL_EVENT, params)
}
